import { Loading } from "../../components/Loading";
import {
  Stack,
  Text,
  Input,
  Textarea,
  Button,
  useToast,
  Box,
  FormLabel,
  FormControl,
  Heading,
  Flex,
  SimpleGrid,
  Icon,
  Checkbox,
} from "@chakra-ui/react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RoleAccessController } from "../../components/RoleAccessController";
import { config } from "../../configs/roles.config";
import { useContext, useState, useEffect } from "react";
import { addRole } from "../../actions/role.actions";
import { getDashboardData } from "../../actions/dashboard.action";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { AuthContext } from "../../providers/AuthValidationLayer";
import {
  getCanonicalOperationsSetupSubHeading,
  normalizeRoleAccessSections,
} from "../../utils/permissionAliases";
import { Unauthorized } from "../../components/Unauthorized";
import { useLocation, useNavigate } from "react-router-dom";
import { MdDragIndicator } from "react-icons/md";
const DroppableColumn = ({ id, title, subtitle, children, isEmpty }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <Box flex={1}>
      <Box mb={4}>
        <Heading size="md" mb={1}>
          {title}
        </Heading>
        <Text fontSize="sm" color="gray.600">
          {subtitle}
        </Text>
      </Box>
      <Box
        ref={setNodeRef}
        minH="400px"
        p={4}
        bg={isOver ? "blue.50" : "gray.50"}
        borderRadius="xl"
        border="2px dashed"
        borderColor={isOver ? "blue.400" : "gray.300"}
        transition="all 0.2s"
      >
        {isEmpty ? (
          <Flex
            h="200px"
            align="center"
            justify="center"
            color="gray.400"
            flexDirection="column"
            gap={2}
          >
            <Text fontSize="lg">🎯</Text>
            <Text fontSize="sm">Drag cards here</Text>
          </Flex>
        ) : (
          <Stack spacing={3}>{children}</Stack>
        )}
      </Box>
    </Box>
  );
};

// Sortable Dashboard Card Component
const SortableDashboardCard = ({ id, card }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      bg="white"
      borderRadius="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
      align="center"
      justify="space-between"
      cursor="grab"
      _active={{ cursor: "grabbing" }}
      _hover={{ shadow: "md", borderColor: "blue.300" }}
      {...attributes}
      {...listeners}
    >
      <Text fontWeight="medium" fontSize="md">
        {card.title}
      </Text>
      <Icon as={MdDragIndicator} w={5} h={5} color="gray.400" />
    </Flex>
  );
};

const CreateRole = () => {
  const { token, hasPermission, user } = useContext(AuthContext);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [allDashboardCards, setAllDashboardCards] = useState([]);
  const [availableCards, setAvailableCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [canAccessDashboardDropdown, setCanAccessDashboardDropdown] = useState(false);
  const [canViewDashboardAs, setCanViewDashboardAs] = useState(false);
  const [canBypassHierarchy, setCanBypassHierarchy] = useState(false);

  // Filter config based on current user's permissions
  const getFilteredConfig = () => {
    const currentUserAccess = user?.roleId?.roleAccess || [];

    // If user is superadmin, show all sections
    if (user?.roleId?.roleName?.toLowerCase() === "superadmin") {
      return config;
    }

    // If no user access, show nothing
    if (currentUserAccess.length === 0) {
      return [];
    }

    // Filter config to only show sections the user has access to
    return config
      .map((section) => ({
        ...section,
        types: section.types.filter((type) =>
          currentUserAccess.some(
            (userSection) =>
              userSection.heading?.toLowerCase() ===
              section.heading.toLowerCase() &&
              userSection.types?.some(
                (userType) =>
                  getCanonicalOperationsSetupSubHeading(
                    userType.subHeading,
                  ).toLowerCase() === type.subHeading.toLowerCase(),
              ),
          ),
        ),
      }))
      .filter((section) => section.types.length > 0);
  };


  const getAllowedAccessMap = () => {
    const currentUserAccess = user?.roleId?.roleAccess || [];
    const allowed = {};

    if (user?.roleId?.roleName?.toLowerCase() === "superadmin") {
      return allowed;
    }

    currentUserAccess.forEach((section) => {
      section.types?.forEach((type) => {
        const canonicalSubHeading =
          getCanonicalOperationsSetupSubHeading(type.subHeading);
        allowed[canonicalSubHeading] = type.access || [];
        allowed[type.subHeading] = type.access || [];
      });
    });

    return allowed;
  };

  const [roleAccess, setRoleAccess] = useState([]);
  const [allowedAccessByType, setAllowedAccessByType] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const source = new URLSearchParams(location.search).get("source");
  const isFromManageRoles = source === "manage-roles";


  // Update roleAccess when user is loaded
  useEffect(() => {
    setRoleAccess(normalizeRoleAccessSections(getFilteredConfig()));
    setAllowedAccessByType(getAllowedAccessMap());
  }, [user]);

  // Ensure create form fully resets when user navigates away and comes back.
  useEffect(() => {
    setRoleName("");
    setRoleDescription("");
    setCanAccessDashboardDropdown(false);
    setCanViewDashboardAs(false);
    setCanBypassHierarchy(false);
    setSelectedCards([]);
    setRoleAccess(normalizeRoleAccessSections(getFilteredConfig()));
    setAllowedAccessByType(getAllowedAccessMap());
  }, [location.key, user]);

  useEffect(() => {
    if (allDashboardCards.length > 0) {
      setAvailableCards(allDashboardCards);
    }
  }, [allDashboardCards, location.key]);

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const dashboardData = await getDashboardData();
        if (dashboardData?.cards) {
          setAllDashboardCards(dashboardData.cards);
          setAvailableCards(dashboardData.cards);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        toast({
          title: "Unable to load dashboard cards",
          description: "Please refresh and try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchDashboardData();
  }, []);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleSelectAllCards = () => {
    setSelectedCards((prevSelected) => {
      const selectedTitles = new Set(prevSelected.map((card) => card.title));
      const cardsToAdd = availableCards.filter(
        (card) => !selectedTitles.has(card.title),
      );
      return [...prevSelected, ...cardsToAdd];
    });
    setAvailableCards([]);
  };

  const handleDeselectAllCards = () => {
    setAvailableCards(availableCards.concat(selectedCards));
    setSelectedCards([]);
  };
  const handleDashboardDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Find which list contains the active card
    const activeInAvailable = availableCards.find(
      (card) => card.title === activeId,
    );
    const activeInSelected = selectedCards.find(
      (card) => card.title === activeId,
    );

    // Determine source and destination containers
    const activeContainer = activeInAvailable ? "available" : "selected";
    const overContainer =
      overId === "available"
        ? "available"
        : overId === "selected"
          ? "selected"
          : selectedCards.find((card) => card.title === overId)
            ? "selected"
            : "available";

    // Moving between containers
    if (activeContainer !== overContainer) {
      const sourceCards =
        activeContainer === "available" ? availableCards : selectedCards;
      const destCards =
        overContainer === "available" ? availableCards : selectedCards;

      const activeCard = sourceCards.find((card) => card.title === activeId);
      const newSourceCards = sourceCards.filter(
        (card) => card.title !== activeId,
      );
      const newDestCards = [...destCards, activeCard];

      if (activeContainer === "available") {
        setAvailableCards(newSourceCards);
        setSelectedCards(newDestCards);
      } else {
        setSelectedCards(newSourceCards);
        setAvailableCards(newDestCards);
      }

      toast({
        title: overContainer === "selected" ? "Card Added" : "Card Removed",
        description:
          overContainer === "selected"
            ? "Card will be shown in dashboard"
            : "Card removed from dashboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
    // Reordering within the same container
    else if (activeId !== overId) {
      if (activeContainer === "selected") {
        setSelectedCards((items) => {
          const oldIndex = items.findIndex((item) => item.title === activeId);
          const newIndex = items.findIndex((item) => item.title === overId);
          return arrayMove(items, oldIndex, newIndex);
        });
      } else {
        setAvailableCards((items) => {
          const oldIndex = items.findIndex((item) => item.title === activeId);
          const newIndex = items.findIndex((item) => item.title === overId);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
  };

  const onAddRole = async () => {
    const trimmedRoleName = roleName.trim();
    const trimmedRoleDescription = roleDescription.trim();
    const nameRegex = /^[a-zA-Z0-9 ]+$/;

    if (!trimmedRoleName || !trimmedRoleDescription) {
      toast({
        title: "Error",
        description: "Role Name or Description not provided",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!nameRegex.test(trimmedRoleName)) {
      toast({
        title: "Invalid Role Name",
        description: "Role name cannot contain special characters",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check if at least one permission is selected
    const hasPermissions = roleAccess.some((section) =>
      section.types.some((type) => type.access && type.access.length > 0),
    );

    if (!hasPermissions) {
      toast({
        title: "Validation Error",
        description:
          "At least one permission must be selected to create a role.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }



    // Only save the cards that are in the right column (selectedCards)
    const SelectedDashboardSections = selectedCards.map((card) => card.section);
    const payload = {
      roleName: trimmedRoleName,
      roleDescription: trimmedRoleDescription,
      roleAccess,
      dashboardAccessKeys: SelectedDashboardSections,
      canAccessDashboardDropdown,
      canViewDashboardAs,
      canBypassHierarchy
    };

    setLoading(true);

    try {
      await addRole(payload, token);

      toast({
        title: "Success",
        description: "New role added",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // redirect after success
      navigate("/settings/roles/manage");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (value, index) => {
    const newState = [...roleAccess];
    newState[index] = value;
    setRoleAccess(newState);
  };

  if (!hasPermission("Access Control", "Roles", "POST")) {
    return <Unauthorized />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box px={6} py={8}>
      <BreadCrumbMaker
        items={
          isFromManageRoles
            ? [
              { link: "/settings", title: "Access Control" },
              { link: "/settings/roles/manage", title: "Manage Roles" },
              {
                link: "/settings/roles/create?source=manage-roles",
                title: "Create Role",
                on: true,
              },
            ]
            : [
              { link: "/settings", title: "Access Control" },
              { link: "/settings/roles/create", title: "Create Role", on: true },
            ]
        }
      />

      <Stack>
        <Box bg="white" borderRadius="2xl" p={6}>
          <Flex justifyContent="space-between">
            <Heading fontSize="lg" mb={4}>
              Role Details
            </Heading>
            <Text color="#90A3BF">Step 1 of {config.length + 2}</Text>
          </Flex>

          <FormControl isRequired mb={4}>
            <FormLabel>Role Name</FormLabel>
            <Input
              value={roleName}
              placeholder="Enter Role Name"
              w={300}
              borderRadius="lg"
              maxLength={50}
              onChange={(e) => setRoleName(e.target.value)}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {roleName.length}/50 characters
            </Text>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={roleDescription}
              placeholder="Role Description"
              w="50%"
              borderRadius="lg"
              resize="none"
              maxLength={255}
              onChange={(e) => setRoleDescription(e.target.value)}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {roleDescription.length}/255 characters
            </Text>
          </FormControl>

        </Box>
        <Box bg="white" borderRadius="2xl" p={6}>
          <Flex justifyContent="space-between" mb={2}>
            <Heading fontSize="lg">Dashboard Configuration</Heading>
            <Text color="#90A3BF">Step 2 of {config.length + 2}</Text>
          </Flex>

          {/* Dashboard Access Permissions */}
          <Box mb={8}>

            {user?.roleId?.roleName?.toLowerCase() === "superadmin" && (
              <FormControl mb={4}>
                <Checkbox
                  isChecked={canBypassHierarchy}
                  onChange={(e) => setCanBypassHierarchy(e.target.checked)}
                >
                  <Flex flexDirection="column" gap={1}>
                    <Text fontWeight="600">Bypass Hierarchy Restrictions</Text>
                    <Text fontSize="sm" color="gray.600" fontWeight="400">
                      Allows this role to access dashboard features across all hierarchy levels
                    </Text>
                  </Flex>
                </Checkbox>
              </FormControl>
            )}

            <FormControl mb={4}>
              <Checkbox
                isChecked={canAccessDashboardDropdown}
                onChange={(e) => setCanAccessDashboardDropdown(e.target.checked)}
              >
                <Flex flexDirection="column" gap={1}>
                  <Text fontWeight="600">Access Dashboard Role Dropdown</Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="400">
                    Enables this role to use the dashboard role dropdown filter feature
                  </Text>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={canViewDashboardAs}
                onChange={(e) => setCanViewDashboardAs(e.target.checked)}
              >
                <Flex flexDirection="column" gap={1}>
                  <Text fontWeight="600">Visible in Dashboard Dropdown</Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="400">
                    Makes this role appear as an option in other roles' dashboard dropdown filters
                  </Text>
                </Flex>
              </Checkbox>
            </FormControl>
          </Box>

          {!canBypassHierarchy ? (
            <>
              <Text fontSize="sm" color="gray.600" mb={6}>
                Drag cards from left to right to add them to this role's dashboard
              </Text>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDashboardDragEnd}
              >
                <Box>
                  <Checkbox
                    onChange={(e) =>
                      e.target.checked
                        ? handleSelectAllCards()
                        : handleDeselectAllCards()
                    }
                    mr={2}
                    my={4}
                    isChecked={availableCards.length === 0}
                    className="text-[14px] font-lg"
                  >
                    Select All Cards
                  </Checkbox>
                </Box>
                <Flex gap={6} direction={{ base: "column", lg: "row" }}>
                  <DroppableColumn
                    id="available"
                    title="Available Cards"
                    subtitle="Drag cards to the right to add them"
                    isEmpty={availableCards.length === 0}
                  >
                    <SortableContext
                      items={availableCards.map((card) => card.title)}
                      strategy={rectSortingStrategy}
                    >
                      {availableCards.map((card) => (
                        <SortableDashboardCard
                          key={card.title}
                          id={card.title}
                          card={card}
                        />
                      ))}
                    </SortableContext>
                  </DroppableColumn>

                  <DroppableColumn
                    id="selected"
                    title="Dashboard Cards"
                    subtitle="These cards will be shown in the dashboard"
                    isEmpty={selectedCards.length === 0}
                  >
                    <SortableContext
                      items={selectedCards.map((card) => card.title)}
                      strategy={rectSortingStrategy}
                    >
                      {selectedCards.map((card) => (
                        <SortableDashboardCard
                          key={card.title}
                          id={card.title}
                          card={card}
                        />
                      ))}
                    </SortableContext>
                  </DroppableColumn>
                </Flex>
              </DndContext>
            </>
          ) : (
            <Box p={6} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
              <Text fontSize="sm" color="blue.700">
                <Text as="span" fontWeight="600">Dashboard card configuration is not required</Text> when bypass hierarchy restrictions is enabled. This role will have access to all dashboard cards automatically.
              </Text>
            </Box>
          )}
        </Box>

        {roleAccess.map((item, index) => (
          <RoleAccessController
            key={item.heading + index}
            data={item}
            step={index + 3}
            totalSteps={roleAccess.length + 2}
            onChange={(value) => handleRoleChange(value, index)}
            allowedAccessByType={allowedAccessByType}
          />
        ))}

        <Button onClick={onAddRole} borderRadius="lg">
          Add
        </Button>
      </Stack>
    </Box>
  );
};

export default CreateRole;
